// ============================================
// Automatic gift manager
// ============================================

(function () {
  'use strict';

  let isProcessing = false;
  let processTimer;
  const config = buildConfig(window.giftManagerConfig || {});
  const productHandles = window.giftProductHandles || {};

  function buildConfig(rawConfig) {
    const domSubtotalConfig = readSubtotalConfigFromDom();
    const domLinkedConfig = readLinkedConfigFromDom();
    const rawMappings = normalizeLinkedMappings(rawConfig.linkedProductsMappings);
    const linkedMappings = mergeMappings(rawMappings, domLinkedConfig.mappings);
    const rawGiftBySubtotalThreshold = toNumber(rawConfig.giftBySubtotalThreshold);
    const rawGiftBySubtotalProductId = extractNumericId(rawConfig.giftBySubtotalProductId);
    const rawGiftBySubtotalVariantId = extractNumericId(rawConfig.giftBySubtotalVariantId);

    return {
      giftBySubtotalEnabled: toBool(rawConfig.giftBySubtotalEnabled) || domSubtotalConfig.enabled,
      giftBySubtotalThreshold: domSubtotalConfig.hasThreshold
        ? domSubtotalConfig.threshold
        : rawGiftBySubtotalThreshold,
      giftBySubtotalProductId: domSubtotalConfig.productId || rawGiftBySubtotalProductId,
      giftBySubtotalVariantId: domSubtotalConfig.variantId || rawGiftBySubtotalVariantId,
      linkedProductsEnabled:
        toBool(rawConfig.linkedProductsEnabled) || domLinkedConfig.enabled || linkedMappings.length > 0,
      linkedProductsMappings: linkedMappings,
    };
  }

  function readSubtotalConfigFromDom() {
    const sectionElements = Array.from(document.querySelectorAll('[data-linked-products-config]'));
    let enabled = false;
    let hasThreshold = false;
    let threshold = 0;
    let productId = '';
    let variantId = '';

    for (const sectionElement of sectionElements) {
      enabled = enabled || toBool(sectionElement.dataset.subtotalGiftEnabled);

      const rawThreshold = sectionElement.dataset.subtotalGiftThreshold;
      if (rawThreshold !== undefined && String(rawThreshold).trim() !== '') {
        hasThreshold = true;
        threshold = toNumber(rawThreshold);
      }

      const currentProductId = extractNumericId(sectionElement.dataset.subtotalGiftProduct);
      const currentVariantId = extractNumericId(sectionElement.dataset.subtotalGiftVariant);

      if (!productId && currentProductId) productId = currentProductId;
      if (!variantId && currentVariantId) variantId = currentVariantId;
    }

    return { enabled, hasThreshold, threshold, productId, variantId };
  }

  function readLinkedConfigFromDom() {
    const sectionElements = Array.from(document.querySelectorAll('[data-linked-products-config]'));
    let enabled = false;
    const mappings = [];

    for (const sectionElement of sectionElements) {
      enabled = enabled || toBool(sectionElement.dataset.linkedProductsEnabled);
      const sectionMappings = Array.from(sectionElement.querySelectorAll('[data-link-rule]'))
        .map((ruleElement) =>
          normalizeSingleMapping({
            // New attributes
            triggerProductId: ruleElement.dataset.triggerProduct,
            triggerVariantId: ruleElement.dataset.triggerVariant,
            giftProductId: ruleElement.dataset.giftProduct,
            giftVariantId: ruleElement.dataset.giftVariant,
            // Legacy attributes
            trigger: ruleElement.dataset.trigger,
            gift: ruleElement.dataset.gift,
          })
        )
        .filter(Boolean);

      mappings.push(...sectionMappings);
    }

    const metafieldElements = Array.from(document.querySelectorAll('[data-linked-products-metafields]'));
    for (const metafieldElement of metafieldElements) {
      enabled = enabled || toBool(metafieldElement.dataset.enabled);
      const rawRules = parseMaybeJson(metafieldElement.dataset.rules || '[]');
      if (!Array.isArray(rawRules)) continue;

      const metafieldMappings = rawRules
        .map((rule) =>
          normalizeSingleMapping({
            triggerVariantId: rule.trigger_variant_id ?? rule.triggerVariantId ?? rule.trigger,
            giftVariantId: rule.gift_variant_id ?? rule.giftVariantId ?? rule.gift,
            triggerProductId: rule.trigger_product_id ?? rule.triggerProductId,
            giftProductId: rule.gift_product_id ?? rule.giftProductId,
          })
        )
        .filter(Boolean);

      mappings.push(...metafieldMappings);
    }

    return { enabled, mappings };
  }

  function mergeMappings(primaryMappings, secondaryMappings) {
    const merged = [];
    const seen = new Set();

    for (const mapping of [...primaryMappings, ...secondaryMappings]) {
      const key = `${mapping.triggerKey}::${mapping.giftVariantId || mapping.giftProductId || ''}`;
      if (seen.has(key)) continue;

      seen.add(key);
      merged.push(mapping);
    }

    return merged;
  }

  function parseMaybeJson(value) {
    if (Array.isArray(value) || (value && typeof value === 'object')) return value;
    if (typeof value !== 'string') return [];

    const trimmed = value.trim();
    if (!trimmed) return [];

    try {
      return JSON.parse(trimmed);
    } catch (error) {
      console.error('Invalid linked product mappings JSON:', error);
      return [];
    }
  }

  function normalizeLinkedMappings(rawMappings) {
    const parsed = parseMaybeJson(rawMappings);
    if (!Array.isArray(parsed)) return [];

    return parsed
      .map((mapping) => normalizeSingleMapping(mapping))
      .filter(Boolean);
  }

  function normalizeSingleMapping(mapping) {
    if (!mapping || typeof mapping !== 'object') return null;

    const legacyTrigger = extractNumericId(mapping.trigger);
    const legacyGift = extractNumericId(mapping.gift);
    const triggerVariantId = extractNumericId(
      mapping.triggerVariantId ?? mapping.trigger_variant_id ?? mapping.triggerVariantID
    );
    const giftVariantId = extractNumericId(
      mapping.giftVariantId ?? mapping.gift_variant_id ?? mapping.giftVariantID
    );
    const triggerProductId = extractNumericId(
      mapping.triggerProductId ?? mapping.trigger_product_id ?? mapping.triggerProductID
    );
    const giftProductId = extractNumericId(
      mapping.giftProductId ?? mapping.gift_product_id ?? mapping.giftProductID
    );

    const resolvedTriggerVariantId = triggerVariantId || legacyTrigger;
    const resolvedGiftVariantId = giftVariantId || legacyGift;

    const triggerKey = triggerProductId || resolvedTriggerVariantId;
    if (!triggerKey || (!resolvedGiftVariantId && !giftProductId)) return null;

    return {
      triggerKey,
      triggerVariantId: resolvedTriggerVariantId,
      giftVariantId: resolvedGiftVariantId,
      triggerProductId,
      giftProductId,
    };
  }

  function toBool(value) {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') return value.toLowerCase() === 'true';
    return Boolean(value);
  }

  function toNumber(value) {
    const normalizedValue = typeof value === 'string' ? value.replace(',', '.') : value;
    const parsed = Number.parseFloat(normalizedValue);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function scheduleProcessCart(delayMs) {
    clearTimeout(processTimer);
    processTimer = setTimeout(processCart, delayMs);
  }

  async function processCart() {
    if (isProcessing) return;
    isProcessing = true;

    try {
      const cart = await fetchCart();

      if (config.giftBySubtotalEnabled && (config.giftBySubtotalVariantId || config.giftBySubtotalProductId)) {
        await handleGiftBySubtotal(cart);
      }

      if (config.linkedProductsEnabled && config.linkedProductsMappings.length > 0) {
        await handleLinkedProducts(cart);
      }
    } catch (error) {
      console.error('Error while processing cart gifts:', error);
    } finally {
      isProcessing = false;
    }
  }

  async function handleGiftBySubtotal(cart) {
    const hasGift = cart.items.some((item) => item.properties && item.properties._auto_gift_subtotal === 'true');
    const subtotal = cart.items_subtotal_price || cart.total_price;
    const threshold = config.giftBySubtotalThreshold * 100;

    if (subtotal >= threshold && !hasGift) {
      if (config.giftBySubtotalVariantId) {
        await addGiftVariant(config.giftBySubtotalVariantId, { _auto_gift_subtotal: 'true' });
      } else {
        await addGiftByProduct(config.giftBySubtotalProductId, { _auto_gift_subtotal: 'true' });
      }
    } else if (subtotal < threshold && hasGift) {
      await removeGiftByProperty(cart, '_auto_gift_subtotal');
    }
  }

  async function handleLinkedProducts(cart) {
    let currentCart = cart;

    for (const mapping of config.linkedProductsMappings) {
      const triggerQuantity = getTriggerQuantity(currentCart, mapping);
      const giftQuantity = getLinkedGiftQuantity(currentCart, mapping.triggerKey);

      if (triggerQuantity > giftQuantity) {
        const quantityToAdd = triggerQuantity - giftQuantity;
        if (mapping.giftVariantId) {
          await addGiftVariant(mapping.giftVariantId, { _linked_gift: mapping.triggerKey }, quantityToAdd);
        } else {
          await addGiftByProduct(mapping.giftProductId, { _linked_gift: mapping.triggerKey }, quantityToAdd);
        }
        currentCart = await fetchCart();
      } else if (triggerQuantity < giftQuantity) {
        const quantityToRemove = giftQuantity - triggerQuantity;
        await removeGiftByProperty(currentCart, '_linked_gift', mapping.triggerKey, quantityToRemove);
        currentCart = await fetchCart();
      }
    }
  }

  function getTriggerQuantity(cart, mapping) {
    return cart.items.reduce((total, item) => {
      if (item.properties && item.properties._linked_gift) return total;

      const matchesTrigger = mapping.triggerProductId
        ? extractNumericId(item.product_id) === mapping.triggerProductId
        : extractNumericId(item.variant_id) === mapping.triggerVariantId;

      if (!matchesTrigger) return total;
      return total + Number(item.quantity || 0);
    }, 0);
  }

  function getLinkedGiftQuantity(cart, triggerKey) {
    return cart.items.reduce((total, item) => {
      if (!item.properties || item.properties._linked_gift !== triggerKey) return total;
      return total + Number(item.quantity || 0);
    }, 0);
  }

  async function addGiftByProduct(productId, properties, quantity) {
    const normalizedProductId = extractNumericId(productId);
    if (!normalizedProductId) return;

    const handle = resolveProductHandle(productId, normalizedProductId);
    if (!handle) {
      // If no handle was provided from Liquid, try using the id as variant id.
      await addGiftVariant(normalizedProductId, properties, quantity);
      return;
    }

    try {
      const productResponse = await fetch(`/products/${handle}.js`);
      const product = await productResponse.json();
      const variantId = product && product.variants && product.variants[0] && product.variants[0].id;
      if (!variantId) return;

      await addGiftVariant(variantId, properties, quantity);
    } catch (error) {
      console.error('Could not resolve product handle for gift:', error);
    }
  }

  function resolveProductHandle(rawProductId, normalizedProductId) {
    if (productHandles[rawProductId]) return productHandles[rawProductId];
    if (productHandles[normalizedProductId]) return productHandles[normalizedProductId];

    for (const [mappedId, handle] of Object.entries(productHandles)) {
      if (extractNumericId(mappedId) === normalizedProductId) return handle;
    }

    return '';
  }

  async function addGiftVariant(variantId, properties, quantity) {
    const normalizedVariantId = extractNumericId(variantId);
    if (!normalizedVariantId) return;
    const normalizedQuantity = Number(quantity || 1);
    if (!Number.isFinite(normalizedQuantity) || normalizedQuantity <= 0) return;

    try {
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({
          items: [
            {
              id: Number(normalizedVariantId),
              quantity: normalizedQuantity,
              properties: properties,
            },
          ],
        }),
      });

      if (!addResponse.ok) {
        const errorText = await addResponse.text();
        console.error('Error adding gift to cart:', errorText);
        return;
      }

      await updateCart();
    } catch (error) {
      console.error('Error adding gift variant:', error);
    }
  }

  async function removeGiftByProperty(cart, propertyKey, propertyValue, quantityToRemove) {
    const expectedValue = propertyValue === undefined ? 'true' : propertyValue;
    const matchingGiftItems = cart.items.filter(
      (item) => item.properties && item.properties[propertyKey] === expectedValue
    );

    if (!matchingGiftItems.length) return;

    const targetRemoveQuantity =
      quantityToRemove === undefined
        ? matchingGiftItems.reduce((total, item) => total + Number(item.quantity || 0), 0)
        : Number(quantityToRemove);

    if (!Number.isFinite(targetRemoveQuantity) || targetRemoveQuantity <= 0) return;

    try {
      let remainingToRemove = targetRemoveQuantity;
      for (const giftItem of matchingGiftItems) {
        if (remainingToRemove <= 0) break;

        const lineQuantity = Number(giftItem.quantity || 0);
        if (lineQuantity <= 0) continue;

        const removeFromLine = Math.min(lineQuantity, remainingToRemove);
        const nextLineQuantity = lineQuantity - removeFromLine;

        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({
            id: giftItem.key,
            quantity: nextLineQuantity,
          }),
        });

        remainingToRemove -= removeFromLine;
      }

      await updateCart();
    } catch (error) {
      console.error('Error removing gift from cart:', error);
    }
  }

  async function fetchCart() {
    const cartResponse = await fetch('/cart.js');
    return cartResponse.json();
  }

  async function updateCart() {
    const cart = await fetchCart();

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gift-manager', cartData: cart });
    }

    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));

    // Keep this fallback for themes/views that do not redraw cart sections from events.
    setTimeout(() => window.location.reload(), 500);
  }

  function extractNumericId(rawId) {
    if (rawId === null || rawId === undefined) return '';
    const asString = String(rawId);
    if (!asString) return '';
    const numericMatch = asString.match(/\d+$/);
    return numericMatch ? numericMatch[0] : '';
  }

  function patchFetchForCartEvents() {
    if (window.__giftManagerFetchPatched) return;
    window.__giftManagerFetchPatched = true;

    const originalFetch = window.fetch;
    window.fetch = function patchedFetch(resource, init) {
      return originalFetch.call(this, resource, init).then((response) => {
        const url = typeof resource === 'string' ? resource : resource && resource.url ? resource.url : '';

        if (/\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url)) {
          scheduleProcessCart(250);
        }

        return response;
      });
    };
  }

  function init() {
    console.log('[GiftManager] linked enabled:', config.linkedProductsEnabled, 'rules:', config.linkedProductsMappings);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => scheduleProcessCart(250));
    }

    document.addEventListener('cart:updated', () => scheduleProcessCart(250));
    document.addEventListener('theme:cart:change', () => scheduleProcessCart(250));

    patchFetchForCartEvents();
    scheduleProcessCart(1200);
  }

  init();
})();
